import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Chip, Grid, Paper, Typography, alpha, useTheme } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { ScrollableBox } from '../CreateShipment/styles';
import ProductTable from '../CreateShipment/ShipmentDetails/ProductTable';
import SingleBoxView from './SingleBoxView';
import { useSelector } from 'react-redux';
import { RootStateType } from '../../store';
import { BoxType } from '../../store/reducers/Shipment/Type';
import { UNIT, calculateWeights } from '../../services/calculateVolume';
import ViewAction from './ViewAction';
import { customWeightRounding } from '../../utils/convertWeight';

interface PackagingDataProps {
  label: string;
  value: string;
  icon: React.ReactNode;
}

function PackagingData({ label, value, icon }: PackagingDataProps) {
  const theme = useTheme();
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: '12px',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          bgcolor: theme.palette.background.paper,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.02)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.75,
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.05)',
            borderColor: alpha(theme.palette.primary.main, 0.35),
          },
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '10px',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: 'common.black',
              lineHeight: 1.3,
              mb: 0.5,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              color: 'primary.main',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {value}
          </Typography>
        </Box>
      </Paper>
    </Grid>
  );
}

export function PackagingDetails() {
  const theme = useTheme();
  const { shipmentById } = useSelector((state: RootStateType) => state.shipment);
  const [selectedBox, setSelectedBox] = useState<BoxType>();
  const [totalShipmentValue, setTotalShipmentValue] = useState<number>(0);
  const [actualWeight, setActualWeight] = useState<number>(0);
  const [viewMore, setViewMore] = useState<boolean>(true);
  const { chareableWeight } = useSelector((state: RootStateType) => state.getRates);
  const boxes = shipmentById?._packageId?.box;
  const lengthUnit: UNIT = shipmentById?._packageId?.unitOfMeasurement?.split('-')?.[1] === UNIT.cm ? UNIT.cm : UNIT.in;
  const weightUnit = shipmentById?._packageId?.unitOfMeasurement?.split('-')?.[0] || 'kg';

  useEffect(() => {
    if (boxes?.length) {
      setSelectedBox({ ...boxes[0], id: '0' });
      const { totalValue, totalActualWeight } = calculateWeights<BoxType>({
        boxes,
        unit: lengthUnit,
      });
      setTotalShipmentValue(totalValue);
      setActualWeight(totalActualWeight);
    }
  }, [boxes, lengthUnit]);

  const formattedChargeableWeight = useMemo(() => {
    if (!chareableWeight?.boxWiseArr) {
      return `0.00 ${weightUnit}`;
    }
    const isSingle =
      chareableWeight.minChargeableWt != null &&
      chareableWeight.maxChargeableWt != null &&
      chareableWeight.minChargeableWt === chareableWeight.maxChargeableWt;

    if (isSingle) {
      const val =
        weightUnit === 'lb'
          ? customWeightRounding(chareableWeight.maxChargeableWt * 2.20462)
          : chareableWeight.maxChargeableWt.toFixed(2);
      return `${val} ${weightUnit}`;
    }

    const minVal =
      weightUnit === 'lb'
        ? customWeightRounding((chareableWeight.minChargeableWt ?? 0) * 2.20462)
        : (Number(chareableWeight.minChargeableWt) || 0).toFixed(2);

    const maxVal =
      weightUnit === 'lb'
        ? customWeightRounding((chareableWeight.maxChargeableWt ?? 0) * 2.20462)
        : (chareableWeight.maxChargeableWt ?? 0).toFixed(2);

    return `${minVal} - ${maxVal} ${weightUnit}`;
  }, [chareableWeight, weightUnit]);

  return (
    <Card
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: '100%',
        borderRadius: '14px',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
        bgcolor: theme.palette.background.paper,
        overflow: 'hidden',
        mb: 3,
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Inventory2OutlinedIcon />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                Packaging Details
              </Typography>
              <Chip
                label={`${boxes?.length || 0} ${boxes?.length === 1 ? 'Box' : 'Boxes'}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                }}
              />
            </Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
              Box dimensions, weights, and declared products
            </Typography>
          </Box>
        </Box>
        <ViewAction setViewMore={setViewMore} viewMore={viewMore} />
      </Box>

      {/* Card Content */}
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Grid container spacing={2}>
          <PackagingData
            label="No of Boxes"
            value={`${boxes?.length || 0}`}
            icon={<Inventory2OutlinedIcon sx={{ fontSize: 22 }} />}
          />
          <PackagingData
            label="Chargeable weight"
            value={formattedChargeableWeight}
            icon={<ScaleOutlinedIcon sx={{ fontSize: 22 }} />}
          />
          <PackagingData
            label="Actual weight"
            value={`${actualWeight} ${weightUnit}`}
            icon={<FitnessCenterOutlinedIcon sx={{ fontSize: 22 }} />}
          />
          <PackagingData
            label="Shipment value"
            value={`${shipmentById?._packageId?.currency || ''} ${totalShipmentValue}`}
            icon={<PaymentsOutlinedIcon sx={{ fontSize: 22 }} />}
          />
        </Grid>

        {viewMore && (
          <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
            <Grid container spacing={2}>
              <Grid item lg={4} xl={3} md={4} sm={12} xs={12}>
                <ScrollableBox container id="fadedScroll" sx={{ pr: { xs: 0, md: 1 } }} rowGap={2}>
                  {boxes?.map((box: BoxType, i: number) => (
                    <SingleBoxView
                      key={`single-box-${i}`}
                      box={{ ...box, id: String(i) }}
                      setSelectedBox={setSelectedBox}
                      isSelected={selectedBox?.id === String(i)}
                    />
                  ))}
                </ScrollableBox>
              </Grid>
              <Grid item lg={8} xl={9} md={8} sm={12} xs={12}>
                {selectedBox?.products && (
                  <Box
                    sx={{
                      borderRadius: '12px',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      bgcolor: theme.palette.background.paper,
                      overflow: 'hidden',
                      p: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <ProductTable
                      title="Product Description"
                      enableActions={false}
                      products={selectedBox.products}
                      destinationCountry={
                        shipmentById?._consigneeId?.country || (shipmentById as any)?.consignee?.country
                      }
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default PackagingDetails;
